import React, { useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
// Material
import { Box, Button, InputAdornment, Snackbar, Typography } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
// Project
import { composedComponent, formatRut } from 'utils/functions';
import { appActions } from 'commons/reducer/commons.reducer';
import { useForm } from 'utils/hooks/useForm';
import { PasswordField } from 'commons/components/PasswordField/PasswordField';
import ButtonWithLoader from 'commons/containers/ButtonWithLoader/ButtonWithLoader';
import { VALIDATIONS } from 'utils/validations';
import { SubmitForm } from 'commons/components/SubmitForm/SubmitForm';
import Card from '@mui/material/Card';
import tattersallLogo from 'utils/images/logo-tattersall.png';
import { BRAND_BLUE, BRAND_BLUE_DARK } from 'modules/main/Login2/components/AuthLayout';
import styles from './styles';
import { registerActions } from './reducer/register.reducer';
import saga from './saga/register.saga';
import FailModal from './components/FailModal/FailModal';

function Register({ controls, actions, error, errorMsg, success, successMsg }) {
  const history = useHistory();
  const { onChange, onSubmit, errors } = useForm(controls, actions, {
    bailByDefault: true,
    prettyErrors: true,
    validations: {
      taxNumber: [VALIDATIONS.REQUIRED, VALIDATIONS.RUT, VALIDATIONS.LENGTH(7, 20)],
      email: [VALIDATIONS.REQUIRED, VALIDATIONS.EMAIL, VALIDATIONS.LENGTH(2, 50)],
      name: [VALIDATIONS.REQUIRED, VALIDATIONS.LENGTH(2, 30)],
      lastName: [VALIDATIONS.REQUIRED, VALIDATIONS.LENGTH(2, 20)],
      secondLastName: [VALIDATIONS.REQUIRED, VALIDATIONS.LENGTH(2, 20)],
      phone: [VALIDATIONS.REQUIRED, VALIDATIONS.LENGTH(7, 9)],
      password: [VALIDATIONS.SIGNUP_PASSWORD],
      passwordConfirmation: [
        VALIDATIONS.SIGNUP_PASSWORD,
        { check: (value) => value === controls.password, message: 'Debe ser igual a contraseña' },
      ],
    },
  });

  const onBlurTrim = useCallback(
    (event) => {
      event.target.value = event.target.value.trim();
      if (event.persist) event.persist();
      onChange(event);
    },
    [onChange],
  );

  const onBlurTaxNumber = useCallback(
    (event) => {
      event.target.value = formatRut(event.target.value, true);
      if (event.persist) event.persist();
      onChange(event);
    },
    [onChange],
  );

  function onRegister() {
    onSubmit(() => actions.register(controls));
  }

  const onSubmitForm = () => {
    if (controls.policiesChecked) onRegister();
  };

  const termsLabel = (
    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.4, fontSize: '1rem' }}>
      Al registrarme, declaro haber leído, comprendido y aceptado en su totalidad las{' '}
      <a
        href="https://plgonline.plataformagroup.cl/webtattersall/online/documentos/politicas_privacidad.pdf?_dc=1603294437749"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BRAND_BLUE, fontWeight: 600 }}>
        políticas de privacidad
      </a>{' '}
      del sitio web
    </Typography>
  );

  // Tamaño de fuente estándar de los campos (input, label y helper).
  const fieldProps = {
    fullWidth: true,
    variant: 'outlined',
    size: 'medium',
    InputProps: { sx: { fontSize: '1.05rem' } },
    InputLabelProps: { sx: { fontSize: '1.05rem' } },
    FormHelperTextProps: { sx: { fontSize: '0.85rem' } },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 4 },
        background: `linear-gradient(135deg, ${BRAND_BLUE_DARK} 0%, #072a68 100%)`,
      }}>
      <FailModal
        isOpen={controls.error}
        controls={controls}
        onClose={() => actions.setFailModal()}
      />

      <Card
        sx={{
          width: '100%',
          maxWidth: 940,
          borderRadius: 4,
          overflow: 'hidden',
          boxSizing: 'border-box',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
        }}>
        {/* Panel lateral de marca (oculto en móvil) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
            p: 4,
            background: `linear-gradient(160deg, ${BRAND_BLUE} 0%, ${BRAND_BLUE_DARK} 100%)`,
            color: '#fff',
          }}>
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: 3,
              p: 2,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}>
            <img
              src={tattersallLogo}
              alt="logo"
              style={{ width: '85%', cursor: 'pointer' }}
              onClick={() => history.push('/')}
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center' }}>
            Bienvenido
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', opacity: 0.85, lineHeight: 1.6 }}>
            Crea tu cuenta para participar en remates, gestionar tus garantías y recibir tus
            devoluciones.
          </Typography>
        </Box>

        {/* Panel del formulario */}
        <Box
          component={SubmitForm}
          onSubmit={onSubmitForm}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: { xs: 3, sm: 4.5 },
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}>
          <Box>
            <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: '1.6rem' }}>
              Crear cuenta
            </Typography>
            <Typography sx={{ color: 'text.secondary', mt: 0.5, fontSize: '1rem' }}>
              Completa tus datos para registrarte.
            </Typography>
          </Box>

          {/* Grilla de campos con CSS grid nativo (sin overflow lateral). */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}>
            <TextField
              {...fieldProps}
              error={!!errors.taxNumber}
              helperText={errors.taxNumber}
              label="Rut"
              id="taxNumber"
              value={controls.taxNumber}
              onBlur={onBlurTaxNumber}
              onChange={onChange}
            />
            <TextField
              {...fieldProps}
              error={!!errors.email}
              helperText={errors.email}
              label="Correo electrónico"
              id="email"
              type="email"
              value={controls.email}
              onChange={onChange}
            />
            <TextField
              {...fieldProps}
              error={!!errors.name}
              helperText={errors.name}
              label="Nombre"
              id="name"
              type="text"
              value={controls.name}
              onBlur={onBlurTrim}
              onChange={onChange}
            />
            <TextField
              {...fieldProps}
              error={!!errors.lastName}
              helperText={errors.lastName}
              label="Apellido paterno"
              id="lastName"
              value={controls.lastName}
              onBlur={onBlurTrim}
              onChange={onChange}
            />
            <TextField
              {...fieldProps}
              error={!!errors.secondLastName}
              helperText={errors.secondLastName}
              label="Apellido materno"
              id="secondLastName"
              value={controls.secondLastName}
              onBlur={onBlurTrim}
              onChange={onChange}
            />
            <TextField
              {...fieldProps}
              error={!!errors.phone}
              helperText={errors.phone}
              label="Teléfono"
              id="phone"
              value={controls.phone}
              onChange={onChange}
              InputProps={{
                sx: { fontSize: '1.05rem' },
                startAdornment: <InputAdornment position="start">+56</InputAdornment>,
              }}
            />
            <PasswordField
              {...fieldProps}
              error={!!errors.password}
              label="Contraseña"
              helperText={
                errors.password ?? 'Mínimo 6 carácteres, 1 mayúscula, 1 minúscula y 1 número'
              }
              id="password"
              value={controls.password}
              onChange={onChange}
            />
            <PasswordField
              {...fieldProps}
              error={!!errors.passwordConfirmation}
              helperText={errors.passwordConfirmation}
              label="Confirmar contraseña"
              id="passwordConfirmation"
              value={controls.passwordConfirmation}
              onChange={onChange}
            />
          </Box>

          <FormControlLabel
            sx={{ alignItems: 'flex-start', m: 0 }}
            control={
              <Checkbox
                checked={controls.policiesChecked}
                onChange={onChange}
                color="primary"
                id="policiesChecked"
                sx={{ pt: 0 }}
              />
            }
            label={termsLabel}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
              mt: 0.5,
            }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => history.goBack()}
              sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '1rem' }}>
              Volver
            </Button>
            <ButtonWithLoader
              buttonId="register"
              ButtonProps={{
                variant: 'contained',
                color: 'primary',
                disabled: !controls.policiesChecked,
                onClick: onRegister,
                sx: { textTransform: 'none', px: 4, borderRadius: 2, fontSize: '1.05rem' },
              }}>
              Registrarse
            </ButtonWithLoader>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 0.5 }}>
            <Link
              to="/login"
              style={{
                textDecoration: 'none',
                color: BRAND_BLUE,
                fontWeight: 600,
                fontSize: '1rem',
              }}>
              ¿Tienes una cuenta? Iniciar sesión
            </Link>
          </Box>
        </Box>
      </Card>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={error}
        autoHideDuration={3000}
        onClose={() => actions.toggleError()}>
        <Alert onClose={() => actions.toggleError()} severity="error">
          {errorMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={success}
        autoHideDuration={3000}
        onClose={() => actions.toggleSuccess()}>
        <Alert onClose={() => actions.toggleSuccess()} severity="success">
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withStyles(
  composedComponent(Register, saga, {
    saga: 'sagaRegister',
    states: ['register.controls', 'app.errorMsg', 'app.error', 'app.successMsg', 'app.success'],
    actions: [registerActions, appActions],
  }),
  styles,
);
